import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from, Subject, catchError, switchMap } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUser} from 'amazon-cognito-identity-js';
import { AuthAmplify } from './auth.amplify';
import { AuthResponse } from './auth.types';
import { User } from 'app/models/user.type';
import { SignUpParams } from '@aws-amplify/auth';
import { ISignUpResult } from 'amazon-cognito-identity-js';
import { TranslocoService } from '@ngneat/transloco';
import { CloudUserService } from '../user/cloud.user.service';
import { Router } from '@angular/router';
import LogRocket from 'logrocket';
import { CloudUser } from '../user/user.types';

const camelcaseKeysDeep = require('camelcase-keys-deep');

@Injectable({ providedIn: 'root'})
export class AuthService implements OnDestroy
{
    // private _authenticated: boolean = false;
    private _signOutInProgress: boolean = false;
    private _signUpResult: ISignUpResult;
    private _forgotPasswordEmail: string;
    private _forgotPasswordResult: any;
    private _currentUser: User;
    private _currentCloudUser: CloudUser;
    private _currentCognitoUser: CognitoUser;

    private _authState: Subject<CognitoUser|any> = new Subject<CognitoUser|any>();
    // eslint-disable-next-line @typescript-eslint/member-ordering
    authState: Observable<CognitoUser|any> = this._authState.asObservable();
    jwtHelper: any;


    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _translateService: TranslocoService,
        private _cloudUserService: CloudUserService,
        private _router: Router,
    )
    {
        const unsubscribe = Hub.listen('auth',(data) => {
            const { channel, payload } = data;

            if (channel === 'auth') {
                switch(payload.event)
                {
                    case 'signIn':
                        const user = payload.data;

                        AuthAmplify.getUserData(user).then(
                            (userData: User) => {
                                console.log("auth.service constructor signIn userService set-user");
                                this._userService.user = userData;
                            },
                            (error) => {
                                // console.log('Hub signIn event getUserData error', error);
                            }
                        );

                        break;
                    case 'signOut':
                        break;
                    case 'customOAuthState':
                        // get customState return from federated login
                        // and set active language from the passing custom state
                        const customState = JSON.parse(payload.data);
                        this._translateService.setActiveLang(customState.lang);
                        if (customState.redirectUri) { // follow code from EVCMS 2.0 by Jauhar              
                            setTimeout(() => {
                              console.log('customState.redirectURL', customState.redirectUri)
                              this._router.navigate([customState.redirectUri])
                            }, 200);
                        }
              
                        break;
                }

                this._authState.next(payload.event);
            }
          });
    }
    ngOnDestroy() {   
        // console.log('Destroy the Auth service');
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get signupEmail(): string
    {
        if (!this._signUpResult) {
            return null;
        }

        return this._signUpResult.user.getUsername();
    }

    get forgotPasswordDestination(): string
    {
        if (!this._forgotPasswordResult) {
            return null;
        }

        return this._forgotPasswordResult.CodeDeliveryDetails.Destination;
    }

    get signupEmailDestination(): string
    {
        if (!this._signUpResult) {
            return null;
        }

        return this._signUpResult.codeDeliveryDetails.Destination;
    }

    get forgotPasswordEmail(): string
    {
        return this._forgotPasswordEmail;
    }

    get activeLanguage(): string
    {
        return localStorage.getItem('activeLanguage') ?? 'en';
    }

    get currentCognitoUser(): CognitoUser 
    {
        return this._currentCognitoUser;
    }

    get currentUser(): any
    {
        return this._currentUser;
    }

    get currentCloudUser(): CloudUser
    {
        return this._currentCloudUser;
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    set activeLanguage(lang: string) {
        localStorage.setItem('activeLanguage', lang);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        const promise = new Promise<boolean>((resolve, reject) => {
            Auth.forgotPassword(email).then(
                (response) => {
                    this._forgotPasswordEmail = email;
                    this._forgotPasswordResult = response;
                    resolve(response);
                },
                (error) => {
                    this._forgotPasswordEmail = null;
                    reject(error);
                }
            );
        });

        return from(promise);

        //return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(user: {email: string; code: string; password: string}): Observable<any>
    {
        const promise = new Promise<boolean>((resolve, reject) => {
            Auth.forgotPasswordSubmit(user.email, user.code, user.password).then(
                (success) => {
                    resolve(true);
                },
                (error) => {
                    reject(error);
                }
            );
        });

        return from(promise);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {

        const promise = new Promise<AuthResponse | any>((resolve, reject) => {

            Auth.signOut({global: true}).then(
                (result) => {
                    Auth.signIn(credentials.email, credentials.password).then(
                        (user: CognitoUser ) => {

                            this._currentCognitoUser = user;

                            AuthAmplify.getUserData(user).then(
                                async (userData: User) => {
                                    console.log("auth.service signIn userService set-user");
                                    this._userService.user = userData;
                                    this._currentUser = userData;

                                    const userItem = await this._cloudUserService.getCurrentUser();
                                    const cloudUser = camelcaseKeysDeep(userItem.item);
                                    this._currentCloudUser = cloudUser;
                                    this._userService.cloudUser = this._currentCloudUser;

                                    const response: AuthResponse = {
                                        accessToken: AuthAmplify.getAccessToken(user),
                                        user: userData,
                                        tokenType: 'bearer'
                                    };
                                    resolve(response);
                                },
                                (error) => {
                                    reject(error);
                                }
                            );
                        },
                        (error) => {
                            reject(error);
                        }
                    );
                },
                (error) => {
                    Auth.signIn(credentials.email, credentials.password).then(
                        (user: CognitoUser ) => {

                            this._currentCognitoUser = user;

                            AuthAmplify.getUserData(user).then(
                                async (userData: User) => {
                                    console.log("auth.service signIn error userService set-user");
                                    this._userService.user = userData;
                                    this._currentUser = userData;

                                    const userItem = await this._cloudUserService.getCurrentUser();
                                    const cloudUser = camelcaseKeysDeep(userItem.item);
                                    this._currentCloudUser = cloudUser;
                                    this._userService.cloudUser = this._currentCloudUser;

                                    const response: AuthResponse = {
                                        accessToken: AuthAmplify.getAccessToken(user),
                                        user: userData,
                                        tokenType: 'bearer'
                                    };
                                    resolve(response);
                                },
                                (err) => {
                                    reject(err);
                                }
                            );
                        },
                        (err) => {
                            reject(err);
                        }
                    );
                }
            );
        });

        return from(promise);
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        if (this._signOutInProgress) {
            return of(true);
        }

        this._signOutInProgress = true;
        const promise = new Promise<boolean>((resolve, reject) => {
            Auth.signOut().then(
                    (success) => {

                        this._signOutInProgress = false;

                        resolve(true);
                    },
                    (error) => {
                        this._signOutInProgress = false;
                        reject(error);
                    }
                );
        });

        // Return the observable
        return from(promise);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string; companyType: string }): Observable<any>
    {

        const promise = new Promise<CognitoUser>((resolve, reject) => {

            const signupParams: SignUpParams = {
                username: user.email,
                password: user.password,
                attributes: {
                    'email': user.email,
                    'name': user.name,
                    'custom:company': user.company,
                    'custom:companyType': user.companyType,
                }
            };

            Auth.signUp(signupParams).then(
                (signupResult) => {

                    this._signUpResult = signupResult;
                    resolve(signupResult.user);
                },
                (error) => {
                    reject(error);
                }
            );
        });

        return from(promise);
        //return this._httpClient.post('api/auth/sign-up', user);
    }

    confirmSignUp(user: { email: string; code: string }): Observable<any>
    {
        const promise = new Promise<boolean>((resolve, reject) => {
            Auth.confirmSignUp(user.email, user.code).then(
                (success) => {
                    resolve(true);
                },
                (error) => {
                    reject(error);
                }
            );
        });

        return from(promise);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {

        const promise = new Promise<boolean>((resolve, reject) => {
            Auth.currentSession().then(
                (session) => {
                    if (this._currentCloudUser !== undefined &&
                        this._currentUser !== undefined
                    ) {
                        resolve(session.isValid());
                        return;
                    }

                    console.log("currentUser is nil => retrieve the current authenticated user", this._currentUser);

                    Auth.currentAuthenticatedUser().then(
                        (user) => {
                            AuthAmplify.getUserData(user).then(
                                async (userData) => {
                                    console.log("auth.service check userService set-user");
                                    this._userService.user = userData;
                                    this._currentUser = userData;

                                    const userItem = await this._cloudUserService.getCurrentUser();
                                    const cloudUser = camelcaseKeysDeep(userItem.item);
                                    this._currentCloudUser = cloudUser;
                                    this._userService.cloudUser = this._currentCloudUser;

                                    // This is an example script - don't forget to change it!
                                    LogRocket.identify('THE_USER_ID_IN_YOUR_APP', {
                                        name: this._currentUser.name,
                                        email: this._currentUser.email,
                                        // Add your own custom user variables here, ie:
                                        // subscriptionType: 'pro'
                                    });
                                    resolve(session.isValid());
                                },
                                (err) => {
                                    console.log("error getUserData", err);
                                }
                            );
                        },
                        (err) => {
                            console.log("auth.service currentAuthenticatedUser error", err);
                            resolve(false);
                            return;
                        }
                    );

                },
                (err) => {
                    console.log("auth.service currentSession error", err);
                    resolve(false);
                    return;
                }
            );
        });

        return from(promise);
    }

    async apiCall(apiFunctionTarget, ...params) {
        const promiseResult = new Promise<any>((resolve, reject) => {
          this.check().pipe(
            switchMap(async isSessionValid => {
  
              if (!isSessionValid) {
                this.signOut();
                location.reload();
              }
              
              return apiFunctionTarget(...params);
            }),
            catchError(err => {
              reject(err);
              return of(null);
            })
          ).subscribe({
            next: summaryResult => {
              resolve(summaryResult);
            },
            error: err => {
              reject(err);
            }
          })
        });
  
        return promiseResult;
    }
    
}
